/**
 * lib/contactUtils.js
 * Kontakt yardımcıları: eksik veri kontrolü, portal login üretimi, paylaşım.
 */

/**
 * "Zu pflegende Daten" — eksik/bakım gerektiren alanların listesi.
 * @param {object} lead
 * @returns {Array<{field: string, label: string, critical: boolean}>}
 */
export function getMissingData(lead) {
  if (!lead) return [];
  const missing = [];
  if (!lead.email) missing.push({ field: 'email', label: 'E-Mail-Adresse', critical: true });
  if (!lead.telefon) missing.push({ field: 'telefon', label: 'Telefonnummer', critical: false });
  if (!lead.strasse || !lead.plz || !lead.ort) {
    missing.push({ field: 'adresse', label: 'Rechnungsanschrift (Straße/PLZ/Ort)', critical: false });
  }
  if (lead.liefer_abweichend && (!lead.liefer_strasse || !lead.liefer_plz || !lead.liefer_ort)) {
    missing.push({ field: 'lieferadresse', label: 'Lieferanschrift unvollständig', critical: false });
  }
  if (!lead.ansprechpartner) missing.push({ field: 'ansprechpartner', label: 'Ansprechpartner', critical: false });
  if (!lead.branche) missing.push({ field: 'branche', label: 'Branche', critical: false });
  if (!lead.kundentyp) missing.push({ field: 'kundentyp', label: 'Kundentyp', critical: false });
  return missing;
}

/** Kritik eksik (kırmızı uyarı simgesi) var mı? — şart: e-posta */
export function hasCriticalMissingData(lead) {
  return !lead?.email;
}

// ─── Portal login üretimi ───────────────────────────────────────────────────

/**
 * Kontakt adından kullanıcı adı üretir: "Café Müller GmbH" → "cafe-mueller-4821"
 */
export function generateUsername(lead) {
  const base = (lead?.name || 'kunde')
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .split('-').slice(0, 2).join('-') || 'kunde';
  const suffix = Math.floor(1000 + Math.random() * 9000);
  return `${base}-${suffix}`;
}

/**
 * Güvenli, okunabilir şifre üretir (16 karakter, karışık).
 * crypto.getRandomValues tabanlı.
 */
export function generatePassword(length = 16) {
  const charset = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!#$%&*+';
  const values = new Uint32Array(length);
  crypto.getRandomValues(values);
  let out = '';
  for (let i = 0; i < length; i += 1) {
    out += charset[values[i] % charset.length];
  }
  return out;
}

// ─── Paylaşım ───────────────────────────────────────────────────────────────

/** Zugangsdaten paylaşım metni */
export function buildCredentialsMessage(lead) {
  return [
    `Zugangsdaten für ${lead.name}`,
    '',
    `Benutzername: ${lead.portal_username}`,
    `Passwort: ${lead.portal_password}`,
    '',
    'Bitte bewahren Sie diese Daten sicher auf.',
  ].join('\n');
}

/**
 * Web Share API ile paylaşır (native paylaşım penceresi: E-Mail, WhatsApp,
 * yüklü diğer uygulamalar). Desteklenmiyorsa false döner — çağıran taraf
 * fallback (mailto/wa.me/kopyala) gösterir.
 */
export async function shareCredentials(lead) {
  const text = buildCredentialsMessage(lead);
  if (navigator.share) {
    try {
      await navigator.share({ title: `Zugangsdaten — ${lead.name}`, text });
      return true;
    } catch (err) {
      // Kullanıcı iptal ettiyse sorun değil
      if (err?.name === 'AbortError') return true;
      return false;
    }
  }
  return false;
}

/** mailto: fallback linki */
export function buildMailtoLink(lead) {
  const subject = encodeURIComponent(`Ihre Zugangsdaten — ${lead.name}`);
  const body = encodeURIComponent(buildCredentialsMessage(lead));
  return `mailto:${lead.email || ''}?subject=${subject}&body=${body}`;
}

/** WhatsApp fallback linki */
export function buildWhatsAppLink(lead) {
  const text = encodeURIComponent(buildCredentialsMessage(lead));
  const phone = (lead.telefon || '').replace(/[^0-9]/g, '');
  return phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
}
